import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Building2,
  Users,
  QrCode,
  TrendingUp,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  BadgeCheck,
  Bookmark,
  Eye,
  Tag,
  Loader2,
  BarChart3,
  ScanLine,
} from "lucide-react";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { useAdminEvent } from "../../hooks/useAdminEvent";
import { companiesService } from "../../../services/companiesService";
import { catalogService } from "../../../services/catalogService";
import { adminAnalyticsApi } from "../../../services/adminAnalyticsApi";
import type { Company } from "../../../types";
import { CompanyAvatar } from "../../../components/CompanyAvatar";

export function ExhibitorDashboard() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";
  const { event, loading: eventLoading } = useAdminEvent(eventId || undefined);

  const [company, setCompany] = useState<Company | undefined>();
  const [repCount, setRepCount] = useState(0);
  const [standScans, setStandScans] = useState(0);
  const [favorites, setFavorites] = useState(0);
  const [profileViews, setProfileViews] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId || !companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [comp, reps, analytics] = await Promise.all([
          companiesService.getById(companyId, eventId, "admin"),
          catalogService.listRepresentatives(eventId, companyId, "admin").catch(() => []),
          adminAnalyticsApi.getExhibitor(eventId, companyId).catch(() => null),
        ]);
        if (!cancelled) {
          setCompany(comp);
          setRepCount(reps.length);
          const a = analytics?.analytics;
          setStandScans(a?.contactScans ?? 0);
          setFavorites(a?.productFavorites ?? 0);
          setProfileViews(a?.companyViews ?? 0);
        }
      } catch {
        if (!cancelled) {
          setCompany(undefined);
          setRepCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, companyId]);

  if (loading || eventLoading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!company || !event) {
    return (
      <div className="p-6 text-gray-500 text-[14px]">Компания не найдена</div>
    );
  }

  const stats = [
    {
      label: "Сканирований стенда",
      value: standScans,
      sub: "за всё мероприятие",
      icon: <QrCode size={18} className="text-indigo-500" />,
      bg: "bg-indigo-50",
    },
    {
      label: "Представителей",
      value: repCount,
      sub: "активных бейджей",
      icon: <Users size={18} className="text-violet-500" />,
      bg: "bg-violet-50",
    },
    {
      label: "Сохранений в избранное",
      value: favorites,
      sub: "товары посетителями",
      icon: <Bookmark size={18} className="text-sky-500" />,
      bg: "bg-sky-50",
    },
    {
      label: "Просмотров профиля",
      value: profileViews,
      sub: "всего просмотров",
      icon: <Eye size={18} className="text-emerald-500" />,
      bg: "bg-emerald-50",
    },
  ];

  const actions = [
    { label: "Профиль компании", href: "/exhibitor/company", icon: <Building2 size={15} /> },
    { label: "Товары на выставке", href: "/exhibitor/products", icon: <Tag size={15} /> },
    { label: "Представители", href: "/exhibitor/reps", icon: <Users size={15} /> },
    { label: "Скачать QR и бейджи", href: "/exhibitor/qr", icon: <QrCode size={15} /> },
    { label: "Вас сканировали · лиды", href: "/exhibitor/leads", icon: <TrendingUp size={15} /> },
    { label: "Вы сканировали", href: "/exhibitor/you-scanned", icon: <ScanLine size={15} /> },
    { label: "Аналитика интереса", href: "/exhibitor/analytics", icon: <BarChart3 size={15} /> },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-[20px] text-gray-900">Мой стенд</h1>
        <p className="text-[13px] text-gray-500 mt-0.5">
          {event.title} · {event.city}, {event.venueName}
        </p>
        <p className="text-[12px] text-indigo-600 mt-1.5">
          Профиль компании, контакты, статистика. Раздел нельзя редактировать. Правки вносит
          организатор
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
        <div className="flex items-start gap-4">
          <CompanyAvatar
            name={company.name}
            logoUrl={company.logoUrl}
            size="xl"
            className="rounded-2xl flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-[17px] text-gray-900">{company.name}</h2>
            <p className="text-[13px] text-gray-500">{company.category}</p>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-[12px] text-gray-400">
              <span className="flex items-center gap-1">
                <MapPin size={11} /> Стенд {company.booth} · Пав. {company.hall}
              </span>
              {company.contact.phone && (
                <span className="flex items-center gap-1">
                  <Phone size={11} /> {company.contact.phone}
                </span>
              )}
              {company.contact.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail size={11} /> {company.contact.email}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1.5 text-[12px] text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Участвует в выставке
          </span>
          <span className="flex items-center gap-1.5 text-[12px] text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full">
            <BadgeCheck size={12} />
            {repCount} представителей
          </span>
          {company.country && (
            <span className="text-[12px] text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
              {company.country}
            </span>
          )}
        </div>
      </div>

      {company.shortAbout ? (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-[13px] text-gray-700 mb-2">Описание стенда</h3>
          <p className="text-[13px] text-gray-600 leading-relaxed">{company.shortAbout}</p>
        </div>
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => navigate("/exhibitor/analytics")}
            className="bg-white rounded-xl border border-gray-100 p-3.5 text-left hover:border-indigo-100 transition-colors"
          >
            <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-2.5`}>
              {s.icon}
            </div>
            <div className="text-[20px] text-gray-900 leading-none">{s.value}</div>
            <div className="text-[12px] text-gray-500 mt-1 leading-snug">{s.label}</div>
            <div className="text-[11px] text-gray-400">{s.sub}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-50">
          <h3 className="text-[13px] text-gray-700">Разделы кабинета</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {actions.map((a) => (
            <button
              key={a.href}
              type="button"
              onClick={() => navigate(a.href)}
              className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-indigo-500 flex-shrink-0">
                {a.icon}
              </div>
              <span className="flex-1 text-[13px] text-gray-800">{a.label}</span>
              <ArrowRight size={14} className="text-gray-300" />
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
